import { useState } from "react";
import { markEntered, type ApiError, type EntryRow } from "../api";
import { CopyGlyph, DASH, ErrorBox } from "../ui";
import { tracked } from "../useAsync";

/**
 * Marks to type into Classroom by hand — the old Entry page, then a view, then the To enter filter
 * of Assignments, and since 2026-10-13 three actions on every Ready row (`IntoClassroom`, below) —
in an assignment's Handed in list, under its Ready filter.
 * What lives here is the actions themselves.
 *
 * 🔴 A BOT CAN NEVER WRITE A GRADE TO COURSEWORK A TEACHER CREATED IN THE CLASSROOM UI —
 * `associatedWithDeveloper` is fixed at creation — and the Classroom API has no comment method at
 * all, so the private comment stays human too. The job here is the deep link and a Copy button
 * beside it, one student at a time.
 *
 * 🔴 A FAILED MARK ENTERED IS SHOWN ON ITS ROW (2026-10-03). It used to vanish: the button's
 * promise had a `.finally` and no `.catch`, and `tracked` re-throws, so a 409 ("reviewed at 75")
 * or a 403 became an unhandled rejection and the row simply stayed as it was. The server's
 * sentence names the remedy, and the reader is the person who needs it.
 */
export function useEntryActions(courseId: string, onEntered: () => void) {
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<Record<string, ApiError>>({});

  const mark = (r: EntryRow) => {
    setBusy(r.submission_id);
    setFailed((f) => { const next = { ...f }; delete next[r.submission_id]; return next; });
    tracked(markEntered(courseId, { submission_id: r.submission_id,
                                    course_work_id: r.course_work_id, points: r.points }))
      // Recording a mark as entered by hand takes it out of every count that says "waiting".
      .then(() => onEntered())
      .catch((e: unknown) => setFailed((f) => ({ ...f, [r.submission_id]: e as ApiError })))
      .finally(() => setBusy(null));
  };

  const copy = (r: EntryRow) => {
    // 🔴 COPIES `student_note` AND NEVER `teacher_note`. The second was written after the agent
    // read an adversarially-controlled repository.
    navigator.clipboard.writeText(r.student_note)
      .then(() => setCopied(r.submission_id))
      .catch(() => setCopied(null));
  };

  return { copied, busy, failed, mark, copy };
}

/** Why Mark entered is not offered to somebody without Release — said once, above the rows. */
export const CAN_ENTER = "Recording a mark as typed into Classroom decides what a student has, "
  + "so it needs the Release permission on this cohort.";

/**
 * Copy note, Open in Classroom and Mark entered, for one Ready row — what To enter's row did, and
 * since 2026-10-13 a column of an assignment's Handed in list under its Ready filter.
 *
 * ⚠️ COPY IS AN ICON (owner, 2026-10-13), with the student's name in what it is called, and it
 * says Copied in its name once done. It copies `student_note` and never `teacher_note`.
 * ⚠️ NOTHING WITHOUT AN ENTRY ROW: a Ready grade always has one (the entry queue is every
 * measured, unreleased grade), so its absence is a read still on its way, and a button that
 * recorded nothing would be worse than none.
 */
export function IntoClassroom({ e, student, may, act }: {
  e: EntryRow | undefined; student: string; may: boolean;
  act: ReturnType<typeof useEntryActions>;
}) {
  if (!e) return <span className="muted">{DASH}</span>;
  const copied = act.copied === e.submission_id;
  const err = act.failed[e.submission_id] ?? null;
  const who = student || "this student";
  return (
    <div className="asg-into">
      <div className="asg-into-acts">
        <button type="button" className="btn sm asg-copy" disabled={!e.student_note}
                onClick={() => act.copy(e)} title={copied ? "Copied" : "Copy note"}
                aria-label={copied ? `Copied the note for ${who}` : `Copy the note for ${who}`}>
          {copied ? <span aria-hidden="true">✓</span> : <CopyGlyph />}</button>
        {e.alternate_link && (
          <a className="btn sm" href={e.alternate_link} target="_blank" rel="noreferrer">
            Open in Classroom ↗</a>)}
        <button type="button" className="btn sm" disabled={!may || act.busy != null}
                onClick={() => act.mark(e)}>
          {act.busy === e.submission_id ? "Recording…" : "Mark entered"}</button>
      </div>
      {err && <ErrorBox err={err} />}
    </div>);
}
